import React from 'react';
import styled from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionMargins,
  standardColours,
} from '../styles';
import { Container, Heading, Link } from './ui';
import tickIcon from '../images/tick-icon.svg';

const StyledCapabilitiesLearnMore = styled.section`
  ${sectionMargins(undefined, '100px')}
`;

const StyledInner = styled.div`
  background-color: ${brandColours.primary};
  border-radius: 12px;
  padding: 30px 20px 20px 20px;

  ${minBreakpointQuery.small`
    padding: 40px 30px 30px 30px;
  `}

  ${minBreakpointQuery.medium`
    padding: 60px 40px 40px 40px;
  `}

  ${minBreakpointQuery.large`
    padding: 80px 50px 50px 50px;
  `}
`;

const StyledHeading = styled(Heading)`
  margin-bottom: 30px;
  text-align: center;

  ${minBreakpointQuery.small`
    margin-bottom: 40px;
  `}

  ${minBreakpointQuery.medium`
    margin-bottom: 50px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 60px;
  `}
`;

const StyledItems = styled.div`
  display: flex;
  gap: 20px;

  ${maxBreakpointQuery.smedium`
    flex-direction: column;
  `}

  ${minBreakpointQuery.small`
    gap: 25px;
  `}

  ${minBreakpointQuery.medium`
    gap: 30px;
  `}

  ${minBreakpointQuery.large`
    gap: 35px;
  `}
  
  ${minBreakpointQuery.xxlarge`
    gap: 40px;
  `}
`;

const StyledItem = styled.div`
  background-color: ${standardColours.white};
  border-radius: 12px;
  padding: 20px;
  width: 100%;

  ${minBreakpointQuery.small`
    padding: 30px;
  `}

  ${minBreakpointQuery.medium`
    padding: 40px;
  `}

  ${minBreakpointQuery.large`
    padding: 55px 50px 50px 50px;
  `}
`;

const StyledSubHeading = styled.h3`
  color: ${brandColours.secondary};
  ${fontSize(18)};
  font-weight: ${fontWeights.semibold};
  margin-bottom: 20px;

  ${minBreakpointQuery.small`
    ${fontSize(20)};
    margin-bottom: 25px;
  `}

  ${minBreakpointQuery.medium`
    ${fontSize(22)};
    margin-bottom: 30px;
  `}

  ${minBreakpointQuery.large`
    ${fontSize(24)};
    margin-bottom: 35px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-bottom: 40px;
  `}
`;

const StyledText = styled.p`
  line-height: 1.4;
  margin-bottom: 15px;

  ${minBreakpointQuery.small`
    ${fontSize(18)};
    margin-bottom: 20px;
  `}

  ${minBreakpointQuery.medium`
    margin-bottom: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 30px;
  `}
`;

const StyledCapabilitiesList = styled.ul`
  display: grid;
  gap: 10px;

  ${minBreakpointQuery.small`
    gap: 15px;
  `}

  ${minBreakpointQuery.large`
    gap: 20px;
  `}
`;

const StyledCapabilityItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;

  ${minBreakpointQuery.small`
    ${fontSize(18)};
  `}

  &:before {
    content: '';
    background: url(${tickIcon}) no-repeat;
    display: inline-block;
    width: 18px;
    height: 18px;
    background-size: 100%;
    flex-shrink: 0;

    ${minBreakpointQuery.small`
      width: 22px;
      height: 22px;
    `}
  }
`;

const StyledLearnMoreList = styled.ul`
  display: grid;
  gap: 15px;

  ${minBreakpointQuery.small`
    gap: 20px;
  `}

  ${minBreakpointQuery.medium`
    gap: 25px;
  `}
  
  ${minBreakpointQuery.large`
    gap: 30px;
  `}
`;

const StyledLearnMoreItem = styled.li`
  ${minBreakpointQuery.small`
    ${fontSize(18)};
  `}
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
`;

const CapabilitiesLearnMore = ({
  capabilities,
  capabilitiesText,
  learnMoreLinks,
  learnMoreLinksText,
}) => {
  const capabilityItems = JSON.parse(capabilities);

  return (
    <StyledCapabilitiesLearnMore>
      <Container>
        <StyledInner>
          <StyledHeading>
            {capabilityItems && capabilityItems.length > 0 && 'Capabilities & '}
            More information
          </StyledHeading>
          <StyledItems>
            {capabilityItems && capabilityItems.length > 0 && (
              <StyledItem>
                <StyledSubHeading>Capabilities of Salesforce</StyledSubHeading>
                <StyledText>{capabilitiesText}</StyledText>
                <StyledCapabilitiesList>
                  {capabilityItems.map((capability, id) => (
                    <StyledCapabilityItem key={id}>
                      {capability}
                    </StyledCapabilityItem>
                  ))}
                </StyledCapabilitiesList>
              </StyledItem>
            )}
            <StyledItem>
              <StyledSubHeading>Learn more about Salesforce</StyledSubHeading>
              <StyledText>{learnMoreLinksText}</StyledText>
              <StyledLearnMoreList>
                {learnMoreLinks.map(({ text, pageUrl }, id) => (
                  <StyledLearnMoreItem key={id}>
                    <StyledLink to={pageUrl}>{text}</StyledLink>
                  </StyledLearnMoreItem>
                ))}
              </StyledLearnMoreList>
            </StyledItem>
          </StyledItems>
        </StyledInner>
      </Container>
    </StyledCapabilitiesLearnMore>
  );
};

export default CapabilitiesLearnMore;
